import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { Dispatch, PropsWithChildren, SetStateAction } from 'react';
import { useDisclosure } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { useUpdateProductItem } from '../../../bff-api/baskets/useUpdateProductItem';
import { BffQueryKey } from '../../../bff-api/model/BffQueryKey';
import { useUpdateCartItems } from './useUpdateCartItems';
import type { Basket, ProductItemEditor } from '../../../bff-api/baskets/model';
import type { DrawerMode } from '../../../contexts/model/CartContext';
import type { Attendee } from '../models/Attendee';
import type { CartCulinaryProductItem } from '../models/CartCulinaryProductItem';

interface CulinaryDrawerContext {
    isEditCulinaryDrawerOpen: boolean;
    onOpen: () => void;
    onClose: () => void;
    culinaryDrawerMode: DrawerMode;
    setCulinaryDrawerMode: Dispatch<SetStateAction<DrawerMode>>;
    editCulinaryItemId: string;
    setEditCulinaryItemId: Dispatch<SetStateAction<string>>;
    editCulinaryAttendee: Attendee | null;
    setEditCulinaryAttendee: Dispatch<SetStateAction<Attendee | null>>;
    editCulinaryIndex: number | null;
    setEditCulinaryIndex: Dispatch<SetStateAction<number | null>>;
}

const Context = createContext<CulinaryDrawerContext>({} as CulinaryDrawerContext);

export const CulinaryDrawerProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [culinaryDrawerMode, setCulinaryDrawerMode] = useState<DrawerMode>('edit');
    const [editCulinaryItemId, setEditCulinaryItemId] = useState<string>('');
    const [editCulinaryIndex, setEditCulinaryIndex] = useState<number | null>(null);
    const [editCulinaryAttendee, setEditCulinaryAttendee] = useState<Attendee | null>(null);

    const ctx = useMemo<CulinaryDrawerContext>(
        () => ({
            isEditCulinaryDrawerOpen: isOpen,
            onOpen,
            onClose,
            culinaryDrawerMode,
            setCulinaryDrawerMode,
            editCulinaryItemId,
            setEditCulinaryItemId,
            editCulinaryAttendee,
            setEditCulinaryAttendee,
            editCulinaryIndex,
            setEditCulinaryIndex,
        }),
        [
            isOpen,
            onOpen,
            onClose,
            culinaryDrawerMode,
            setCulinaryDrawerMode,
            editCulinaryItemId,
            setEditCulinaryItemId,
            editCulinaryAttendee,
            setEditCulinaryAttendee,
            editCulinaryIndex,
            setEditCulinaryIndex,
        ],
    );

    return <Context.Provider value={ctx}>{children}</Context.Provider>;
};

export interface UseCulinaryEditorReturn {
    isEditCulinaryDrawerOpen: boolean;
    onOpenEditCulinaryDrawer: (
        mode: DrawerMode,
        productId: string,
        attendee?: Attendee,
        index?: number,
    ) => void;
    onCloseEditCulinaryDrawer: () => void;
    onDeleteAttendee: (productItem: CartCulinaryProductItem, attendeeId: string) => void;
    onAddAttendee: (
        productItem: CartCulinaryProductItem,
        classAttendees: { attendees: Attendee[] },
    ) => void;
}

export function useCulinaryDrawer(): UseCulinaryEditorReturn &
    Pick<
        CulinaryDrawerContext,
        'culinaryDrawerMode' | 'editCulinaryItemId' | 'editCulinaryAttendee' | 'editCulinaryIndex'
    > {
    const queryClient = useQueryClient();
    const updateProductItem = useUpdateProductItem();
    const { updateProductItemLocally } = useUpdateCartItems();

    const ctx = useContext(Context);

    if (!ctx) {
        throw new Error('useCulinaryDrawer must be used within CulinaryDrawerProvider');
    }

    const {
        setCulinaryDrawerMode,
        setEditCulinaryItemId,
        setEditCulinaryAttendee,
        setEditCulinaryIndex,
        onOpen,
        onClose,
        ...context
    } = ctx;

    const onOpenEditCulinaryDrawer = useCallback(
        (mode: DrawerMode, itemId: string, attendee?: Attendee, index?: number) => {
            setCulinaryDrawerMode(mode);
            setEditCulinaryItemId(itemId);

            if (itemId) {
                setEditCulinaryItemId(itemId);
            }

            if (attendee) {
                setEditCulinaryAttendee(attendee);
            }

            if (index) {
                setEditCulinaryIndex(index);
            }

            onOpen();
        },
        [
            setCulinaryDrawerMode,
            setEditCulinaryItemId,
            setEditCulinaryAttendee,
            setEditCulinaryIndex,
            onOpen,
        ],
    );

    const onCloseEditCulinaryDrawer = useCallback(() => {
        setEditCulinaryItemId('');
        setEditCulinaryIndex(null);
        setEditCulinaryAttendee(null);
        onClose();
    }, [setEditCulinaryItemId, setEditCulinaryIndex, setEditCulinaryAttendee, onClose]);

    const onProductItemUpdateSuccess = useCallback(
        (productItem: CartCulinaryProductItem, payload: Basket): void => {
            queryClient.setQueryData([BffQueryKey.BASKET], payload);
            const quantity: number | undefined = payload?.productItems?.find(
                (item) => item.itemId === productItem.itemId,
            )?.quantity;
            updateProductItemLocally({
                ...productItem,
                ...(typeof quantity === 'number' ? { quantityEditor: quantity } : {}),
            } as ProductItemEditor);
        },
        [queryClient, updateProductItemLocally],
    );

    const onDeleteAttendee = useCallback(
        (productItem: CartCulinaryProductItem, attendeeId: string) => {
            const filteredAttendees =
                productItem?.c_culinaryClass?.attendees.filter(
                    (attendee) => attendee.id !== attendeeId,
                ) ?? [];

            const updatedAttendee = {
                productId: productItem.productId,
                itemId: productItem.itemId,
                c_attendeeDetails: JSON.stringify({ attendees: filteredAttendees }),
                quantity: filteredAttendees.length,
            };

            updateProductItem.mutate(updatedAttendee, {
                onSuccess: (payload) => onProductItemUpdateSuccess(productItem, payload),
            });
        },
        [queryClient, updateProductItem, onProductItemUpdateSuccess],
    );

    const onAddAttendee = useCallback(
        (productItem: CartCulinaryProductItem, classAttendees: { attendees: Attendee[] }) => {
            const updatedAttendee = {
                productId: productItem.productId,
                itemId: productItem.itemId,
                c_attendeeDetails: JSON.stringify(classAttendees),
                quantity: classAttendees.attendees.length,
            };
            updateProductItem.mutate(updatedAttendee, {
                onSuccess: (payload) => onProductItemUpdateSuccess(productItem, payload),
            });
        },
        [queryClient, updateProductItem, onProductItemUpdateSuccess],
    );

    return useMemo(
        () => ({
            ...context,
            onOpenEditCulinaryDrawer,
            onCloseEditCulinaryDrawer,
            onAddAttendee,
            onDeleteAttendee,
        }),
        [
            context,
            onOpenEditCulinaryDrawer,
            onCloseEditCulinaryDrawer,
            onAddAttendee,
            onDeleteAttendee,
        ],
    );
}
